import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import { BillingSidebar } from "../../components/billing/BillingSidebar";
import { AccountingTabs } from "../../components/accounting/AccountingTabs";
import { formatINR } from "../../components/dashboard/formatINR";
import ExpenseAPI, { Expense, ExpenseCategory } from "../../services/expense.service";
import { ExpenseEntryModal } from "../../components/accounting/ExpenseEntryModal";

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export const ExpenseListPage: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(30));
  const [dateTo, setDateTo] = useState(todayIso());
  const [categoryFilter, setCategoryFilter] = useState("");

  const categoryName = useCallback(
    (id: string) => categories.find(c => c.category_id === id)?.name ?? "—",
    [categories]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [expenseList, categoryList] = await Promise.all([
        ExpenseAPI.list({ date_from: dateFrom, date_to: dateTo, category_id: categoryFilter || undefined }),
        ExpenseAPI.listCategories(),
      ]);
      setExpenses(expenseList);
      setCategories(categoryList);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load expenses");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (expense: Expense) => {
    if (!window.confirm(`Delete expense of ${formatINR(expense.amount_paise)}?`)) return;
    try {
      await ExpenseAPI.delete(expense.expense_id);
      toast.success("Expense deleted");
      load();
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete expense");
    }
  };

  const total = expenses.reduce((sum, e) => sum + e.amount_paise, 0);

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Sidebar />
      <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0 min-h-screen">
        <div className="max-w-[1300px] mx-auto px-4 py-6">
          <div className="bg-white/60 border border-[rgba(206,250,254,0.5)] rounded-[20px] shadow-[0px_10px_15px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col md:flex-row min-h-[calc(100vh-7rem)]">

            {/* Billing sub-navigation */}
            <BillingSidebar />

            {/* Main content */}
            <div className="flex-1 px-4 sm:px-8 py-6 sm:py-8 overflow-y-auto">
              <div className="flex flex-col gap-6 max-w-[920px]">

                <AccountingTabs />

                {/* ── Expenses ─────────────────────────────────────────────── */}
                <section className="bg-white/60 border border-[rgba(206,250,254,0.5)] rounded-[14px] shadow-[0px_4px_6px_rgba(0,0,0,0.1)] p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-base font-semibold text-[#0f172b] flex items-center gap-2">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00b8db" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                      Expenses
                    </h2>
                    <p className="text-xs text-slate-500">{expenses.length} entries · Total {formatINR(total)}</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">From</label>
                      <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} max={dateTo}
                             className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">To</label>
                      <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} min={dateFrom}
                             className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Category</label>
                      <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
                              className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30">
                        <option value="">All categories</option>
                        {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <button
                    onClick={() => { setEditingExpense(null); setModalOpen(true); }}
                    className="mb-5 py-2 px-4 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#00b8db] to-[#2b7fff] hover:opacity-90 transition shadow-sm"
                  >
                    + Record Expense
                  </button>

                  <div className="rounded-[14px] border border-[#cefafe] bg-white/60 overflow-hidden">
                    {loading ? (
                      <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
                    ) : expenses.length === 0 ? (
                      <div className="p-8 text-center text-slate-400 text-sm">No expenses in this range.</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[#cefafe] text-left text-slate-500">
                            <th className="px-4 py-3 font-medium">Date</th>
                            <th className="px-4 py-3 font-medium">Category</th>
                            <th className="px-4 py-3 font-medium">Vendor</th>
                            <th className="px-4 py-3 font-medium">Payment</th>
                            <th className="px-4 py-3 font-medium text-right">Amount</th>
                            <th className="px-4 py-3 font-medium text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expenses.map(e => (
                            <tr key={e.expense_id} className="border-b border-[#e6fbfd] last:border-0 hover:bg-[#f0fdfe]">
                              <td className="px-4 py-3 text-slate-700">{e.expense_date}</td>
                              <td className="px-4 py-3 text-slate-700">{categoryName(e.category_id)}</td>
                              <td className="px-4 py-3 text-slate-700">{e.vendor_name || "—"}</td>
                              <td className="px-4 py-3 text-slate-500 capitalize">{e.payment_mode?.replace("_", " ") || "—"}</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-900">{formatINR(e.amount_paise)}</td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => { setEditingExpense(e); setModalOpen(true); }}
                                  className="text-[#007595] hover:underline text-xs mr-3"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(e)}
                                  className="text-red-500 hover:underline text-xs"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </section>

              </div>
            </div>
          </div>
        </div>
      </div>

      <ExpenseEntryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={load}
        categories={categories}
        onCategoryCreated={c => setCategories(prev => [...prev, c])}
        editingExpense={editingExpense}
      />
    </div>
  );
};

export default ExpenseListPage;
